import React from 'react'
import {
  Box,
  Typography,
  Chip,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import { TitleHeroWidgetData } from '../../../types/gotypes'

type Size = 'compact' | 'standard' | 'hero'
type Alignment = 'left' | 'center' | 'right'

const SIZE_CONFIG: Record<Size, { py: number; headingVariant: 'h5' | 'h4' | 'h2'; subtitleVariant: 'body2' | 'h6' | 'h5'; minHeight?: number }> = {
  compact: { py: 2, headingVariant: 'h5', subtitleVariant: 'body2' },
  standard: { py: 4, headingVariant: 'h4', subtitleVariant: 'h6' },
  hero: { py: 8, headingVariant: 'h2', subtitleVariant: 'h5', minHeight: 280 },
}

const PRESET_COLORS = [
  { bg: '#1565c0', text: '#ffffff', label: 'Blue' },
  { bg: '#1a1a2e', text: '#ffffff', label: 'Dark' },
  { bg: '#2e7d32', text: '#ffffff', label: 'Green' },
  { bg: '#6a1b9a', text: '#ffffff', label: 'Purple' },
  { bg: '#c62828', text: '#ffffff', label: 'Red' },
  { bg: '#ef6c00', text: '#ffffff', label: 'Orange' },
  { bg: '#ffffff', text: '#1a1a1a', label: 'White' },
  { bg: '#f5f5f5', text: '#1a1a1a', label: 'Light' },
]

interface TitleHeroWidgetRendererProps {
  data: TitleHeroWidgetData
}

export const TitleHeroWidgetRenderer: React.FC<TitleHeroWidgetRendererProps> = ({ data }) => {
  const size = (data.size as Size) || 'standard'
  const alignment = (data.alignment as Alignment) || 'center'
  const config = SIZE_CONFIG[size] || SIZE_CONFIG.standard
  const bgColor = data.bg_color || '#1565c0'
  const textColor = data.text_color || '#ffffff'

  // Derive a slightly transparent version for subtitle
  const subtitleColor = textColor === '#ffffff' || textColor === '#fff'
    ? 'rgba(255,255,255,0.8)'
    : textColor === '#1a1a1a' || textColor === '#000000'
      ? 'rgba(0,0,0,0.6)'
      : textColor

  // Badge uses a semi-transparent version of the text color
  const badgeBg = textColor === '#ffffff' || textColor === '#fff'
    ? 'rgba(255,255,255,0.15)'
    : 'rgba(0,0,0,0.08)'

  return (
    <Box
      sx={{
        background: bgColor,
        py: config.py,
        px: 4,
        borderRadius: 1,
        textAlign: alignment,
        minHeight: config.minHeight,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {data.badge && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={data.badge}
            size="small"
            sx={{ bgcolor: badgeBg, color: textColor, fontWeight: 500 }}
          />
        </Box>
      )}
      <Typography
        variant={config.headingVariant}
        sx={{ color: textColor, fontWeight: 700, lineHeight: 1.2 }}
      >
        {data.heading}
      </Typography>
      {data.subtitle && (
        <Typography
          variant={config.subtitleVariant}
          sx={{ color: subtitleColor, fontWeight: 400, mt: 1 }}
        >
          {data.subtitle}
        </Typography>
      )}
    </Box>
  )
}

interface TitleHeroWidgetEditorProps {
  data: TitleHeroWidgetData | undefined
  onDataChange: (data: TitleHeroWidgetData) => void
}

export const TitleHeroWidgetEditor: React.FC<TitleHeroWidgetEditorProps> = ({ data, onDataChange }) => {
  const current: TitleHeroWidgetData = data || { heading: '', size: 'standard', alignment: 'center', bg_color: '#1565c0', text_color: '#ffffff' }

  const update = (partial: Partial<TitleHeroWidgetData>) => {
    onDataChange({ ...current, ...partial })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Heading"
        size="small"
        fullWidth
        value={current.heading}
        onChange={(e) => update({ heading: e.target.value })}
      />
      <TextField
        label="Subtitle (optional)"
        size="small"
        fullWidth
        value={current.subtitle || ''}
        onChange={(e) => update({ subtitle: e.target.value })}
      />
      <TextField
        label="Badge (optional)"
        size="small"
        fullWidth
        value={current.badge || ''}
        onChange={(e) => update({ badge: e.target.value })}
        placeholder="e.g. Q1 2026"
      />

      {/* Size */}
      <Box>
        <Typography variant="overline" color="text.secondary">Size</Typography>
        <ToggleButtonGroup
          value={current.size || 'standard'}
          exclusive
          onChange={(_e, val) => val && update({ size: val })}
          size="small"
          fullWidth
        >
          <ToggleButton value="compact">Compact</ToggleButton>
          <ToggleButton value="standard">Standard</ToggleButton>
          <ToggleButton value="hero">Hero</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Alignment */}
      <Box>
        <Typography variant="overline" color="text.secondary">Alignment</Typography>
        <ToggleButtonGroup
          value={current.alignment || 'center'}
          exclusive
          onChange={(_e, val) => val && update({ alignment: val })}
          size="small"
        >
          <ToggleButton value="left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Color presets */}
      <Box>
        <Typography variant="overline" color="text.secondary">Color Preset</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
          {PRESET_COLORS.map((preset) => {
            const isSelected = current.bg_color === preset.bg && current.text_color === preset.text
            return (
              <Box
                key={preset.label}
                onClick={() => update({ bg_color: preset.bg, text_color: preset.text })}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  bgcolor: preset.bg,
                  border: '2px solid',
                  borderColor: isSelected ? 'info.main' : preset.bg === '#ffffff' || preset.bg === '#f5f5f5' ? 'grey.300' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: isSelected ? 'info.main' : 'grey.500' },
                }}
                title={preset.label}
              >
                {isSelected && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: preset.text }} />
                )}
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Custom colors */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Background</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <input
              type="color"
              value={current.bg_color || '#1565c0'}
              onChange={(e) => update({ bg_color: e.target.value })}
              style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {current.bg_color || '#1565c0'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">Text</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <input
              type="color"
              value={current.text_color || '#ffffff'}
              onChange={(e) => update({ text_color: e.target.value })}
              style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {current.text_color || '#ffffff'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Live preview */}
      {current.heading && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Preview</Typography>
          <TitleHeroWidgetRenderer data={current} />
        </>
      )}
    </Box>
  )
}
